/**
 * /hotels/:code — Hotel detail page.
 *
 * Merges Content API detail (description, images, facilities, rooms)
 * with availability rates fetched inline. Facilities are labeled
 * correctly per Hotelbeds' indFee / indYesOrNo / mandatory flags.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Star, MapPin, Phone, Info, CheckCircle2, XCircle, DollarSign } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";

const BOARD_LABELS = { RO: "Room Only", BB: "Breakfast", HB: "Half Board", FB: "Full Board", AI: "All-Inclusive" };

function categoryStars(catCode) {
  if (!catCode) return 0;
  const m = /^(\d)/.exec(catCode);
  return m ? Number(m[1]) : 0;
}

function facilityLabel(f) {
  // Hotelbeds semantics (docs — Content API "hotel facilities"):
  //   indYesOrNo=false        → facility is NOT available at the hotel
  //   indFee=true             → available for a fee (paid)
  //   indFee=false / undefined → available at no charge (free)
  //   mandatory=true          → mandatory (resort fee etc.) —
  //                             stacks on top of paid/free
  if (f.indYesOrNo === false) {
    return { tag: "unavailable", labelText: "Unavailable", cls: "text-tb-navy/40 line-through", pillCls: "border-tb-navy/25 text-tb-navy/45", Icon: XCircle };
  }
  const paid = f.indFee === true;
  const mandatory = f.mandatory === true;
  if (mandatory) {
    return { tag: "mandatory", labelText: paid ? "Mandatory · Paid" : "Mandatory", cls: "text-amber-700", pillCls: "border-amber-500/60 text-amber-800", Icon: DollarSign };
  }
  if (paid) {
    return { tag: "paid", labelText: "Paid", cls: "text-amber-700", pillCls: "border-amber-500/60 text-amber-800", Icon: DollarSign };
  }
  return { tag: "free", labelText: "Free", cls: "text-emerald-700", pillCls: "border-emerald-500/50 text-emerald-800", Icon: CheckCircle2 };
}

export default function HotelDetailPage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const checkIn = sp.get("checkIn");
  const checkOut = sp.get("checkOut");
  const preselectRateKey = sp.get("rateKey");

  const [detail, setDetail] = useState(null);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Parallel: content-API detail + single-hotel availability (uses
    // HB's `hotels.hotel: [code]` filter — returns only this one
    // property so sandbox quota consumption is minimal vs. a full
    // destination sweep).
    const detailReq = api.get(`/hotels/detail/${code}`);
    const availReq = (checkIn && checkOut)
      ? api.post("/hotels/availability", {
          hotelCodes: [Number(code)],
          checkIn, checkOut,
          occupancies: [{ rooms: 1, adults: 2, children: 0, paxes: [{ type: "AD", age: 30 }, { type: "AD", age: 30 }] }],
        }).catch(() => ({ data: { hotels: [] } }))
      : Promise.resolve({ data: { hotels: [] } });
    Promise.all([detailReq, availReq]).then(([dResp, aResp]) => {
      if (!alive) return;
      setDetail(dResp.data);
      const match = (aResp.data.hotels || []).find((h) => String(h.code) === String(code));
      if (match) {
        const rs = (match.rooms || []).flatMap((r) => (r.rates || []).map((rate) => ({ ...rate, roomCode: r.code, roomName: r.name })));
        setRates(rs);
      }
    }).catch((e) => {
      if (!alive) return;
      setErr(formatApiErrorDetail(e.response?.data?.detail));
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, checkIn, checkOut]);

  const stars = categoryStars(detail?.categoryCode);
  const heroImage = detail?.images?.[selectedImage]?.url;

  const roomsByCode = useMemo(() => {
    const groups = {};
    for (const r of rates) {
      const key = r.roomCode || "misc";
      if (!groups[key]) groups[key] = { code: key, name: r.roomName, rates: [] };
      groups[key].rates.push(r);
    }
    return Object.values(groups);
  }, [rates]);

  const goToBook = (rate) => {
    const params = new URLSearchParams({ rateKey: rate.rateKey, rateType: rate.rateType || "BOOKABLE", checkIn, checkOut });
    nav(`/hotels/${code}/book?${params.toString()}`);
  };

  return (
    <div data-testid="hotel-detail-page" className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-20">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>

        {loading ? (
          <p className="mt-16 text-center font-hand text-tb-navy/60">~ loading hotel ~</p>
        ) : err ? (
          <div className="mt-6 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">{err}</div>
        ) : detail ? (
          <>
            {/* Header */}
            <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  {stars > 0 && (
                    <div className="inline-flex items-center gap-0.5">
                      {Array.from({ length: stars }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-tb-gold fill-tb-gold" />
                      ))}
                    </div>
                  )}
                </div>
                <h1 data-testid="hotel-detail-name" className="mt-1 text-tb-navy text-3xl md:text-4xl leading-tight font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {detail.name}
                </h1>
                {detail.address && (
                  <p className="mt-1 font-hand text-tb-navy/60 text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {detail.address}{detail.city ? `, ${detail.city}` : ""}
                  </p>
                )}
              </div>
              <PoweredByHotelbeds />
            </div>

            {/* Gallery */}
            {detail.images?.length > 0 && (
              <div className="mt-6 rounded-2xl overflow-hidden border-2 border-tb-navy/15 bg-white">
                <div className="aspect-[16/9] bg-white">
                  {heroImage && (
                    <img
                      src={heroImage}
                      alt={detail.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {detail.images.length > 1 && (
                  <div className="p-2 flex gap-2 overflow-x-auto">
                    {detail.images.slice(0, 12).map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setSelectedImage(i)}
                        aria-label={`View image ${i + 1}`}
                        className={"shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 " + (i === selectedImage ? "border-tb-navy" : "border-transparent")}
                      >
                        <img src={img.thumbnailUrl || img.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {detail.description && (
              <div className="mt-6 font-hand text-tb-navy/80 text-base leading-relaxed max-w-3xl">
                {detail.description}
              </div>
            )}

            {/* Rates */}
            <section className="mt-8">
              <h2 className="text-tb-navy text-2xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                Available rooms
              </h2>
              {roomsByCode.length === 0 ? (
                <p className="mt-3 font-hand text-tb-navy/55 italic text-sm">
                  {checkIn && checkOut ? "No rates found for these dates." : "Search from /hotels to see live rates."}
                </p>
              ) : (
                <ul data-testid="hotel-detail-rooms" className="mt-4 space-y-4">
                  {roomsByCode.map((rm) => (
                    <li key={rm.code} className="rounded-2xl border-2 border-tb-navy/15 overflow-hidden">
                      <div className="px-4 py-2 bg-white font-hand text-tb-navy text-sm">
                        {rm.name || rm.code}
                      </div>
                      <ul>
                        {rm.rates.slice(0, 5).map((r, i) => {
                          const price = r.net || r.sellingRate;
                          const board = BOARD_LABELS[r.boardCode] || r.boardName || r.boardCode;
                          const cp = (r.cancellationPolicies || [])[0];
                          return (
                            <li key={r.rateKey || i} className="px-4 py-3 flex flex-wrap items-center gap-3 border-t border-tb-navy/15 first:border-t-0">
                              <div className="flex-1 min-w-0">
                                <div className="font-hand text-tb-navy text-sm">{board}</div>
                                <div className="mt-0.5 text-xs text-tb-navy/60 font-hand">
                                  {r.rateType} · {r.paymentType || "AT_WEB"}
                                </div>
                                {cp?.from && (
                                  <div className="mt-1 font-hand text-emerald-700 text-xs">
                                    Free cancellation until {new Date(cp.from).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="font-serif text-tb-navy text-xl leading-none" style={{ fontFamily: '"Playfair Display", serif' }}>
                                  {price} {r.currency || ""}
                                </div>
                                <button
                                  type="button"
                                  data-testid={`hotel-detail-select-rate-${r.rateKey?.slice(0, 20)}`}
                                  onClick={() => goToBook(r)}
                                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-sm tb-tap hover:brightness-110"
                                >
                                  Select
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Facilities */}
            {detail.facilities?.length > 0 && (
              <section className="mt-10">
                <h2 className="text-tb-navy text-2xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Facilities
                </h2>
                <ul data-testid="hotel-detail-facilities" className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-6">
                  {detail.facilities.slice(0, 40).map((f, i) => {
                    const label = facilityLabel(f);
                    return (
                      <li
                        key={i}
                        data-testid={`hotel-detail-facility-${label.tag}`}
                        data-facility-label={label.labelText}
                        className={"flex items-center gap-2 font-hand text-sm py-1 " + label.cls}
                      >
                        <label.Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.9} />
                        <span className="truncate">{f.description || `Facility ${f.facilityCode}`}</span>
                        <span
                          className={"ml-auto shrink-0 text-[10px] uppercase tracking-widest rounded-full border px-1.5 py-0.5 " + label.pillCls}
                        >
                          {label.labelText}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {detail.phones?.length > 0 && (
              <div className="mt-8 font-hand text-tb-navy/70 text-sm flex items-center gap-2">
                <Phone className="w-3.5 h-3.5" /> {detail.phones[0]}
              </div>
            )}

            <div className="mt-8 rounded-xl border border-tb-navy/20 bg-white px-4 py-3 flex items-start gap-2 font-hand text-tb-navy/70 text-xs">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Availability and rates are supplied by Hotelbeds and refreshed in real time. Final price and cancellation terms are locked at confirmation.
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
